import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { HTTPResponseError, UserStateType } from '../types';
import { fetchCurrentUser, isHTTPError } from '../utils/fetch';

export const initialUserState: UserStateType = {
    avatarSrc: null,
    error: null,
    familyName: null,
    givenName: null,
    id: null,
    loading: false
};

/**
 * User slice for controlling updates to the user state.  Autogenerates action creators and
 * action types boilerplate.
 * See https://redux-toolkit.js.org/api/createslice/
 */
export const userSlice = createSlice({
    name: 'user',
    initialState: initialUserState,

    /**
     * Yes, these reducer states are really immutable, though they don't appear to be!
     * Reducer functions use immer under the hood, which lets us write simpler code (without
     * having to manually make copies of the state).
     * See https://redux-toolkit.js.org/usage/immer-reducers#immer-usage-patterns
     */
    reducers: {
        pending: (state) => {
            state.loading = true;
        },
        fulfilled: (state, action: PayloadAction<UserStateType>) => {
            return {
                ...action.payload,
                loading: false,
                error: null
            };
        },
        rejected: (state, action: PayloadAction<HTTPResponseError>) => {
            state.loading = false;
            state.error = action.payload;
        },
        /**
         * Resets back to initial state.  This is often handy to call when some components unmount.
         * In this case we need to reset the state when a user logs out.
         */
        reset: () => initialUserState
    }
});

export const { reset } = userSlice.actions;

/**
 * Async thunk that returns the current user metadata, and fetches it when needed.
 * Thunks are the recommended way to make async updates to the store.
 * See https://redux-toolkit.js.org/api/createAsyncThunk
 */
export const getCurrentUser = createAsyncThunk('user', async (args, thunkApi) => {
    const { getState, rejectWithValue } = thunkApi;
    // The type here isn't entirely correct for the state, but since we only care about
    // the user state, it lets us avoid importing `RootState` and causing circular dependency issues.
    const state = getState() as { user: UserStateType };
    const { id } = state.user;

    // Return cached user already in the store.
    if (id !== null) {
        return state.user;
    }

    // Fetch the user fresh from the server.
    try {
        const response = await fetchCurrentUser();
        return response;
    } catch (error) {
        const errorMessage = isHTTPError(error) ? `${error.code}: ${error.message}` : error;
        return rejectWithValue(errorMessage);
    }
});

export default userSlice.reducer;
